import axios from 'axios';
import { useContext, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pagination } from 'components/Pagination';
import { FieldInput } from 'components/fieldInput';
import {
  Animate,
  Grid,
  Search,
  Table,
  useAPI,
  ViewContext,
} from 'components/lib';
import DateHelper from '../../helpers/dateUtils';
import styles from './styles.module.scss';

export function BindRequests() {
  const navigate = useNavigate();
  const { notification } = useContext(ViewContext);
  const user = useAPI('/api/user');
  const dateFormatter = useMemo(() => new DateHelper(), []);
  
  const [payload, setPayload] = useState(null);
  const [bindRequests, setBindRequests] = useState([]);
  const [agencyId, setAgencyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('Active');
  const [ticketStatusFilter, setTicketStatusFilter] = useState([]);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchQuery), 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Sync agencyId from user
  useEffect(() => {
    if (user?.loading) {
      setLoading(true);
    } else if (user?.data?.account_id) {
      setAgencyId(user.data.account_id);
    }
  }, [user]);

  // Fetch bind requests when dependencies change
  useEffect(() => {
    if (!agencyId) return;
    fetchBindRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedSearch, statusFilter, ticketStatusFilter, agencyId]);

  const fetchBindRequests = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`/api/bind-requests/${agencyId}`, {
        params: {
          page,
          search: debouncedSearch,
          status: statusFilter,
          ticketStatus: ticketStatusFilter,
          sortBy: 'createdAt',
          sortOrder: 'desc',
        },
      });

      if (!data?.success) {
        notification.show('Unexpected API response structure', 'error', true, 'toast', 'x-circle');
        return;
      }

      setPayload(data);
      setBindRequests(
        data.data.map((r) => ({
          id: r.id,
          Number: r.bindNumber,
          'Agency Name': r.agencyName || 'N/A',
          'Company Name': r.companyName || 'N/A',
          'Submission Number': r.submissionNumber || r.id,
          'Created At': r.createdAt ? dateFormatter.timestamp(r.createdAt) : '—',
          'Updated At': r.updatedAt ? dateFormatter.timestamp(r.updatedAt) : '—',
          status: r.status,
        }))
      );
    } catch {
      notification.show('Failed to load bind requests', 'error', true, 'toast', 'x-circle');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setPage(1);
  };

  const statusOptions = ['Active', 'Bound', 'Declined', 'Not Taken Up'];

  return (
    <Animate type="pop">
      <div style={stylesHeader.container}>
        <div style={stylesHeader.tabs}>
          {statusOptions.map((status) => (
            <div
              key={status}
              onClick={() => handleStatusFilter(status)}
              style={{
                ...stylesHeader.tab,
                ...(statusFilter === status
                  ? stylesHeader.tabActive
                  : stylesHeader.tabInactive),
              }}
            >
              {status}
            </div>
          ))}
        </div>
      </div>

      <Grid cols="1">
        <div className={styles.wrapper}>
          <div style={stylesSearch.container}>
            <div style={stylesSearch.search}>
              <Search callback={setSearchQuery} />
            </div>
            <div style={stylesSearch.filter}>
              <FieldInput
                id="ticketStatus_filter"
                type="multi_select"
                label="Filters"
                defaultValue={[]}
                options={[
                  { value: 'Pending', label: 'Pending' },
                  { value: 'In Progress', label: 'In Progress' },
                ]}
                handleChange={(selected) =>
                  setTicketStatusFilter(selected.map((s) => s.value))
                }
              />
            </div>
          </div>

          <Table
            data={bindRequests}
            hide={['id', 'status']}
            loading={loading}
            emptyText={!loading && bindRequests.length === 0 ? 'No records found.' : undefined}
            actions={{
              custom: [
                {
                  icon: 'search',
                  color: '#637381',
                  action: (row) => navigate(`/app/bind-requests/${row.id}`),
                },
              ],
            }}
          />

          {payload && !loading && (
            <Pagination
              pagesCount={payload.pages_count}
              active={payload.page}
              onChange={(newPage) => setPage(newPage)}
            />
          )}
        </div>
      </Grid>
    </Animate>
  );
}

// Reusable inline style objects (for readability)
const stylesHeader = {
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    alignContent: 'center',
    marginBottom: 10,
  },
  tabs: { display: 'flex' },
  tab: {
    marginLeft: 5,
    width: 150,
    padding: 10,
    fontSize: 16,
    fontWeight: 600,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  tabActive: { backgroundColor: 'black', color: 'white' },
  tabInactive: { backgroundColor: '#EAEAEA', color: '#090E3480' },
};

const stylesSearch = {
  container: {
    marginBottom: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  search: { width: '80%', marginTop: 30 },
  filter: { width: '20%', marginLeft: 20 },
};
